/**
 * tRPC Error Parsing Utilities
 *
 * Helpers for extracting structured error data from tRPC errors.
 */
import { TRPCClientError } from '@trpc/client';
import type { ReplacementGarmentStatus, StocktakeDriftedLine } from '@threadheads/shared';

export interface ReplacementInfo {
  batchName: string | null;
  stage: string;
}

export interface RedoErrorData {
  isRedoError: true;
  isInventoryError: false;
  isOldLabel: false;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: false;
  isOrderCancelled: false;
  replacement: ReplacementInfo | null;
}

export interface GenericErrorData {
  isRedoError: false;
  isInventoryError: false;
  isOldLabel: false;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: false;
  isOrderCancelled: false;
  message: string;
}

export interface InventoryErrorData {
  isRedoError: false;
  isInventoryError: true;
  isOldLabel: false;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: false;
  isOrderCancelled: false;
  message: string;
}

export interface OldLabelErrorData {
  isRedoError: false;
  isInventoryError: false;
  isOldLabel: true;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: false;
  isOrderCancelled: false;
  message: string;
}

export interface OrderItemRemovedErrorData {
  isRedoError: false;
  isInventoryError: false;
  isOldLabel: false;
  isOrderItemRemoved: true;
  isOrderChangedRemoved: false;
  isOrderCancelled: false;
  message: string;
}

/**
 * BLU-695: a garment cancelled by cancelSurplusUnits (an order-change drift
 * correction), as opposed to isOrderItemRemoved (an order-sync pre-batch
 * removal) or isOrderCancelled (a full/manual cancel).
 */
export interface OrderChangedRemovedErrorData {
  isRedoError: false;
  isInventoryError: false;
  isOldLabel: false;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: true;
  isOrderCancelled: false;
  message: string;
}

export interface OrderCancelledErrorData {
  isRedoError: false;
  isInventoryError: false;
  isOldLabel: false;
  isOrderItemRemoved: false;
  isOrderChangedRemoved: false;
  isOrderCancelled: true;
  message: string;
}

export type ParsedError = RedoErrorData | InventoryErrorData | OldLabelErrorData | OrderItemRemovedErrorData | OrderChangedRemovedErrorData | OrderCancelledErrorData | GenericErrorData;

/**
 * Parse a tRPC error to extract redo garment info.
 *
 * Backend returns: BAD_REQUEST with message 'REDO_GARMENT: ...'
 * and cause: { code: 'REDO_GARMENT', replacement: {...} }
 *
 * @param error Any error (typically from catch block)
 * @returns Parsed error data with redo detection
 */
export function parseRedoError(error: unknown): ParsedError {
  // Check for REDO_GARMENT or INVENTORY_ERROR in message
  if (error instanceof TRPCClientError) {
    if (error.message.includes('REDO_GARMENT')) {
      // Try to extract replacement info from cause
      let replacement: ReplacementInfo | null = null;

      // Structured cause rides on data.cause (backend errorFormatter forwards
      // it; raw error.cause is the legacy fallback inside readForwardedCause).
      const cause = readForwardedCause(error);
      if (cause?.replacement) {
        replacement = {
          batchName: cause.replacement.batchName ?? null,
          stage: cause.replacement.stage ?? 'Unknown',
        };
      }

      return {
        isRedoError: true,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        replacement,
      };
    }

    // BLU-259: Detect inventory error (order pulled from batch)
    if (error.message.includes('INVENTORY_ERROR')) {
      return {
        isRedoError: false,
        isInventoryError: true,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.message.replace('INVENTORY_ERROR: ', ''),
      };
    }

    // Detect old label from resolved inventory error
    if (error.message.includes('OLD_LABEL:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: true,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.message.replace('OLD_LABEL: ', ''),
      };
    }

    // BLU-285: Detect order item removed by customer edit
    if (error.message.includes('ORDER_ITEM_REMOVED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: true,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.message.replace('ORDER_ITEM_REMOVED: ', ''),
      };
    }

    // BLU-695: detect a surplus unit cancelled by an order-change drift correction
    if (error.message.includes('ORDER_CHANGED_REMOVED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: true,
        isOrderCancelled: false,
        message: error.message.replace('ORDER_CHANGED_REMOVED: ', ''),
      };
    }

    // BLU-308: Detect cancelled order
    if (error.message.includes('ORDER_CANCELLED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: true,
        message: error.message.replace('ORDER_CANCELLED: ', ''),
      };
    }

    return {
      isRedoError: false,
      isInventoryError: false,
      isOldLabel: false,
      isOrderItemRemoved: false,
      isOrderChangedRemoved: false,
      isOrderCancelled: false,
      message: error.message,
    };
  }

  // Handle string errors (e.g., from hooks that store error.message as string)
  if (typeof error === 'string') {
    if (error.includes('REDO_GARMENT')) {
      return {
        isRedoError: true,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        replacement: null, // Can't extract replacement from string
      };
    }
    if (error.includes('INVENTORY_ERROR')) {
      return {
        isRedoError: false,
        isInventoryError: true,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.replace('INVENTORY_ERROR: ', ''),
      };
    }
    if (error.includes('OLD_LABEL:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: true,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.replace('OLD_LABEL: ', ''),
      };
    }
    if (error.includes('ORDER_ITEM_REMOVED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: true,
        isOrderChangedRemoved: false,
        isOrderCancelled: false,
        message: error.replace('ORDER_ITEM_REMOVED: ', ''),
      };
    }
    if (error.includes('ORDER_CHANGED_REMOVED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: true,
        isOrderCancelled: false,
        message: error.replace('ORDER_CHANGED_REMOVED: ', ''),
      };
    }
    if (error.includes('ORDER_CANCELLED:')) {
      return {
        isRedoError: false,
        isInventoryError: false,
        isOldLabel: false,
        isOrderItemRemoved: false,
        isOrderChangedRemoved: false,
        isOrderCancelled: true,
        message: error.replace('ORDER_CANCELLED: ', ''),
      };
    }
    return {
      isRedoError: false,
      isInventoryError: false,
      isOldLabel: false,
      isOrderItemRemoved: false,
      isOrderChangedRemoved: false,
      isOrderCancelled: false,
      message: error,
    };
  }

  // Non-tRPC error
  const message = error instanceof Error ? error.message : 'An unexpected error occurred';
  return {
    isRedoError: false,
    isInventoryError: false,
    isOldLabel: false,
    isOrderItemRemoved: false,
    isOrderChangedRemoved: false,
    isOrderCancelled: false,
    message,
  };
}

/**
 * Check if an error is a station routing error (STATION_MISMATCH or STATION_NOT_ASSIGNED).
 * Returns the cause data if present.
 */
export function parseStationError(error: unknown): {
  isStationError: boolean;
  code?: 'STATION_MISMATCH' | 'STATION_NOT_ASSIGNED';
  correctStation?: string;
  userStation?: string;
} {
  if (error instanceof TRPCClientError) {
    const cause = readForwardedCause(error);
    if (cause?.code === 'STATION_MISMATCH' || cause?.code === 'STATION_NOT_ASSIGNED') {
      return {
        isStationError: true,
        code: cause.code,
        correctStation: cause.correctStation,
        userStation: cause.userStation,
      };
    }
  }
  return { isStationError: false };
}

/**
 * Print station: detect a PAP-product block (`PAP_PRODUCT:` marker). Works on
 * both a TRPCClientError and the string `err.message` the lookup hook stores.
 */
export function parsePapBlocked(error: unknown): boolean {
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  return msg.includes('PAP_PRODUCT');
}

export interface DtfProductCause {
  isDtfProduct: boolean;
  /** Printing it DTG needs pretreat first (the `DTF_PRODUCT:pretreat:` marker). */
  needsPretreat: boolean;
}

/**
 * Print station: detect a DB-confirmed cross-tech block (`DTF_PRODUCT:`
 * marker) — getGarmentForPrint's own guard reading the garment row's
 * print_technology, NOT the scanned label. The DB-trust signal the DTG
 * station uses to open the mismatch dialog instead of a raw dead-end error
 * (a scanned QR's `m` field can go stale after a post-print correction).
 * BLU-1509: `DTF_PRODUCT:pretreat:` says the shirt needs pretreat. Works on
 * both a TRPCClientError and the string `err.message` the lookup hook stores.
 */
export function parseDtfProductCause(error: unknown): DtfProductCause {
  const message = messageOf(error);
  return {
    isDtfProduct: message.includes('DTF_PRODUCT'),
    needsPretreat: message.includes('DTF_PRODUCT:pretreat:'),
  };
}

/**
 * Film-Print station: detect a DB-confirmed "not a DTF unit" rejection —
 * getBatchForGarment's own guard reading the garment row's print_technology,
 * NOT the scanned label. The DB-trust signal the Film-Print station uses to
 * redirect to the DTG printer instead of a generic lookup-failure error.
 * Keys off the `NOT_DTF_UNIT:` marker (getBatchForGarment ONLY) rather than
 * the prose substring "not a DTF unit" — createFilmUnit's prepare-film guard
 * throws that same prose for an unrelated failure mode (a garment already
 * resolved to the wrong batch trying to prepare a film), which this parser
 * must not also catch. Works on both a TRPCClientError and the string
 * `err.message` the lookup hook stores.
 */
export function parseNotDtfUnitBlocked(error: unknown): boolean {
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  return msg.includes('NOT_DTF_UNIT:');
}

export interface PendingLabelsInfo {
  isPendingLabels: boolean;
  batchNumber: number | null;
}

/**
 * Print station: detect a PENDING (QR-labels-not-printed) block and pull the
 * batch number. Backend marker: `PENDING_LABELS:<batchNumber>: …` (+ a
 * `{ code: 'PENDING_LABELS', batchNumber }` cause). The marker string is the
 * resilient path (the lookup hook keeps only `err.message`).
 */
export function parsePendingLabels(error: unknown): PendingLabelsInfo {
  if (error instanceof TRPCClientError) {
    const cause = readForwardedCause(error);
    if (cause?.code === 'PENDING_LABELS') {
      return { isPendingLabels: true, batchNumber: cause.batchNumber ?? null };
    }
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  const match = msg.match(/PENDING_LABELS:(\d*):/);
  if (match) {
    const n = match[1] ? Number(match[1]) : NaN;
    return { isPendingLabels: true, batchNumber: Number.isFinite(n) ? n : null };
  }
  return { isPendingLabels: false, batchNumber: null };
}

export interface OrderOnHoldInfo {
  isOnHold: boolean;
  /** Raw hold-reason token (e.g. 'waiting_for_stock'); null if unknown. */
  holdReason: string | null;
}

/**
 * Part 3: detect an ON_HOLD-order scan block at any station. Backend marker:
 * `ORDER_ON_HOLD:<reason>: …` (+ a `{ code: 'ORDER_ON_HOLD', holdReason }` cause). The marker
 * string is the resilient path (the lookup hooks keep only `err.message`; tRPC does not serialize
 * `cause` to the client). Mirrors parsePendingLabels.
 */
export function parseOrderOnHold(error: unknown): OrderOnHoldInfo {
  if (error instanceof TRPCClientError) {
    const cause = readForwardedCause(error);
    if (cause?.code === 'ORDER_ON_HOLD') {
      return { isOnHold: true, holdReason: cause.holdReason ?? null };
    }
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  const match = msg.match(/ORDER_ON_HOLD:([a-z_]*):/);
  if (match) {
    return { isOnHold: true, holdReason: match[1] || null };
  }
  return { isOnHold: false, holdReason: null };
}

export type HandoverRefusalCode =
  | 'HANDOVER_ORDER'
  | 'NOT_HANDOVER_ORDER'
  | 'HANDOVER_BENCH_MISSING'
  | 'HANDOVER_TAG_SCOPE_MISSING'
  | 'HANDOVER_LAST_BENCH'
  | 'HANDOVER_WRONG_STATUS'
  | 'HANDOVER_ORDER_CHANGED';

const HANDOVER_REFUSAL_MARKER =
  /\b(HANDOVER_ORDER_CHANGED|HANDOVER_ORDER|NOT_HANDOVER_ORDER|HANDOVER_BENCH_MISSING|HANDOVER_TAG_SCOPE_MISSING|HANDOVER_LAST_BENCH|HANDOVER_WRONG_STATUS): /;

/**
 * Hand over refusals. Backend contract (apps/backend/src/modules/shipping/handover-errors.ts):
 * the message is `<CODE>: <plain copy>`. tRPC 11.7.1 drops wrapped causes on the wire, so this
 * reads the message marker ONLY. Returns the code, or null for any other error. Callers show
 * their own translated copy for the code, never the raw message.
 */
export function parseHandoverRefusal(error: unknown): HandoverRefusalCode | null {
  const msg =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
        ? (error as { message: string }).message
        : '';
  const match = msg.match(HANDOVER_REFUSAL_MARKER);
  return match ? (match[1] as HandoverRefusalCode) : null;
}

/** `production` namespace copy for the refusals a packing station can hit. */
const HANDOVER_STATION_COPY_KEYS: Partial<Record<HandoverRefusalCode, string>> = {
  HANDOVER_ORDER: 'shipping.handover.refusedHandoverOrder',
  NOT_HANDOVER_ORDER: 'shipping.handover.refusedNotHandover',
  HANDOVER_WRONG_STATUS: 'shipping.handover.refusedWrongStatus',
  HANDOVER_ORDER_CHANGED: 'shipping.handover.refusedOrderChanged',
};

/**
 * The `production` i18n key for a hand over refusal at a packing station, or
 * null for any other error (the caller then shows its usual message).
 */
export function handoverRefusalCopyKey(error: unknown): string | null {
  const code = parseHandoverRefusal(error);
  return code ? HANDOVER_STATION_COPY_KEYS[code] ?? null : null;
}

/**
 * BLU-1462: a status override tried to send an order a client QC redo reopened
 * back to the queue. Backend contract (production-orders/client-qc-redo.ts):
 * the message starts `QC_REDO_NO_REQUEUE: `. Reads the marker only (tRPC
 * 11.7.1 drops wrapped causes); the caller shows its own translated copy.
 */
export function isQcRedoNoRequeueRefusal(error: unknown): boolean {
  const msg =
    error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : '';
  return /\bQC_REDO_NO_REQUEUE: /.test(msg);
}

export interface FilmArtworkTransientInfo {
  /** The backend tagged this artwork failure as RETRYABLE — a storage/probe
   *  incident (indeterminate HEAD, mirror not landed yet), not a proven
   *  absence. The station retries before it escalates. */
  isTransient: boolean;
  /**
   * The backend tagged the SAME failure family as NON-retryable
   * (`retryable: false` on the cause) — retrying cannot help, so the station
   * skips its retry ladder and escalates on the first attempt.
   */
  isPermanent: boolean;
  /** Short backend detail token for the log line (never rendered raw). */
  detail: string | null;
}

/**
 * Phase 50.1: classify a `filmPrint.prepareFilm` THROW that the backend tagged
 * as an artwork-delivery incident. Backend contract:
 * `cause: { code: 'FILM_ARTWORK_TRANSIENT', retryable: true, detail }` thrown
 * for transient storage/probe incidents (the indeterminate-probe case that
 * used to surface as a flat, non-retryable NOT_FOUND).
 *
 * `retryable` is read, not assumed: the same code carrying `retryable: false`
 * means "this artwork failure is permanent" (corrupt dual row / validation),
 * and the station must escalate immediately instead of burning three attempts.
 * The message marker is the resilient fallback (mirrors parseOrderOnHold);
 * a marker-only hit is treated as retryable, matching the code's intent.
 *
 * Phase 92: `FILM_SUPPLY_BLOCKED:` is the PERMANENT marker for a supply garment
 * whose blank link is broken (or whose 3PL does not own the floor). tRPC drops
 * the `retryable: false` cause on the wire, so the marker alone classifies it
 * permanent with detail `supply_mapping_invalid`. Backend twin:
 * `FILM_SUPPLY_BLOCKED_MARKER` in film-print.service.ts.
 */
export function parseFilmArtworkTransient(error: unknown): FilmArtworkTransientInfo {
  const cause = readForwardedCause(error);
  if (cause?.code === 'FILM_ARTWORK_TRANSIENT') {
    const permanent = cause.retryable === false;
    return {
      isTransient: !permanent,
      isPermanent: permanent,
      detail: cause.detail ?? null,
    };
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  if (msg.includes('FILM_SUPPLY_BLOCKED')) {
    return { isTransient: false, isPermanent: true, detail: 'supply_mapping_invalid' };
  }
  if (msg.includes('FILM_ARTWORK_TRANSIENT')) {
    return { isTransient: true, isPermanent: false, detail: null };
  }
  return { isTransient: false, isPermanent: false, detail: null };
}

export interface FilmSideMismatchInfo {
  isSideMismatch: boolean;
  /** The side this film actually carries — the one the operator must press. */
  requiredSide: 'front' | 'back' | null;
}

/**
 * Phase 50.1: the press station submitted a side this film does not carry
 * (the stale-tab window — Vercel deploys after Railway, so a tab loaded before
 * the back-only fix can still submit 'front' for a back-only film). Backend
 * contract: PRECONDITION_FAILED with
 * `cause: { code: 'FILM_SIDE_MISMATCH', requiredSide }`, plus a
 * `FILM_SIDE_MISMATCH:<side>:` message marker as the resilient fallback.
 * Without this parser the raw PG/RAISE text reaches the press floor.
 */
export function parseFilmSideMismatch(error: unknown): FilmSideMismatchInfo {
  const cause = readForwardedCause(error);
  if (cause?.code === 'FILM_SIDE_MISMATCH') {
    const side = cause.requiredSide;
    return {
      isSideMismatch: true,
      requiredSide: side === 'front' || side === 'back' ? side : null,
    };
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  const match = msg.match(/FILM_SIDE_MISMATCH:(front|back)?/);
  if (match) {
    const side = match[1];
    return { isSideMismatch: true, requiredSide: side === 'front' || side === 'back' ? side : null };
  }
  return { isSideMismatch: false, requiredSide: null };
}

export interface OrderChangedInfo {
  isOrderChanged: boolean;
  /** Which drift kind (line_added | swap | qty_decreased | …); null if unknown. */
  changeKind: string | null;
  /** The ORDER_CHANGED prod_issues row, for a future "take me to it" affordance. */
  issueId: string | null;
}

/**
 * BLU-695: detect an order-change scan block at any station. Backend marker:
 * `ORDER_CHANGED:<kind>: …` (+ a `{ code: 'ORDER_CHANGED', changeKind, issueId }`
 * cause). The marker string is the resilient path — `useGarmentLookup` and
 * `useGarmentPrintLookup` keep only `err.message` and discard `data.cause`, so a
 * cause-only signal would never reach those two stations. Mirrors parseOrderOnHold.
 */
export function parseOrderChanged(error: unknown): OrderChangedInfo {
  if (error instanceof TRPCClientError) {
    const cause = readForwardedCause(error);
    if (cause?.code === 'ORDER_CHANGED') {
      return {
        isOrderChanged: true,
        changeKind: cause.changeKind ?? null,
        issueId: cause.issueId ?? null,
      };
    }
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  const match = msg.match(/ORDER_CHANGED:([a-z_]*):/);
  if (match) {
    return { isOrderChanged: true, changeKind: match[1] || null, issueId: null };
  }
  return { isOrderChanged: false, changeKind: null, issueId: null };
}

/**
 * BLU-197: classify a bench-assignment failure from the catcher station.
 * The backend uses tRPC codes (on `error.data.code`): NOT_FOUND when the scanned
 * bench label doesn't exist, PRECONDITION_FAILED when the bench is already claimed.
 */
export interface BenchAssignError {
  isBenchNotFound: boolean;
  isBenchInUse: boolean;
}

export function parseBenchError(error: unknown): BenchAssignError {
  if (error instanceof TRPCClientError) {
    const code = (error.data as { code?: string } | undefined)?.code;
    if (code === 'NOT_FOUND') return { isBenchNotFound: true, isBenchInUse: false };
    if (code === 'PRECONDITION_FAILED') return { isBenchNotFound: false, isBenchInUse: true };
  }
  return { isBenchNotFound: false, isBenchInUse: false };
}

export interface SupersededErrorData {
  isSuperseded: boolean;
  /** Live replacement garment, or null when the order was reset but not yet re-batched. */
  replacement: ReplacementGarmentStatus | null;
}

/**
 * Read an INTENTIONAL structured cause off a TRPCClientError. tRPC drops
 * `TRPCError.cause` from the wire (getErrorShape keeps only message/code/data),
 * so the backend errorFormatter re-forwards UPPER_SNAKE signal causes onto
 * `data.cause`. Prefer that; fall back to `error.cause` for resilience.
 */
function readForwardedCause(
  error: unknown,
): {
  code?: string;
  replacement?: ReplacementGarmentStatus | null;
  correctStation?: string;
  userStation?: string;
  batchNumber?: number | null;
  holdReason?: string | null;
  changeKind?: string | null;
  issueId?: string | null;
  /** FILM_ARTWORK_TRANSIENT — false means "this artwork failure is permanent". */
  retryable?: boolean;
  /** FILM_ARTWORK_TRANSIENT — short backend detail token (log only). */
  detail?: string | null;
  /** FILM_SIDE_MISMATCH — the side the film actually carries. */
  requiredSide?: string | null;
} | undefined {
  if (!(error instanceof TRPCClientError)) return undefined;
  const dataCause = (error.data as { cause?: unknown } | undefined)?.cause;
  if (dataCause && typeof dataCause === 'object') {
    return dataCause as { code?: string; replacement?: ReplacementGarmentStatus | null };
  }
  const errCause = error.cause;
  if (errCause && typeof errCause === 'object') {
    return errCause as { code?: string; replacement?: ReplacementGarmentStatus | null };
  }
  return undefined;
}

/**
 * SUPERSEDED sticker (2026-07-08): detect a re-batch-invalidated sticker scan at
 * any station. Backend throws BAD_REQUEST `SUPERSEDED_LABEL: …` with cause
 * `{ code: 'SUPERSEDED_LABEL', action, replacement }`. The shirt is fine — a live
 * replacement may exist (offer a one-tap reprint) or the order needs re-batching
 * (replacement null). Mirrors parseRedoError's cause/replacement read, but the
 * structured replacement rides on the forwarded `data.cause` (see
 * readForwardedCause); the message marker is the last-resort fallback (replacement
 * unknown → treated as "not re-batched yet").
 */
export function parseSupersededError(error: unknown): SupersededErrorData {
  const cause = readForwardedCause(error);
  if (cause?.code === 'SUPERSEDED_LABEL') {
    return { isSuperseded: true, replacement: cause.replacement ?? null };
  }
  const msg =
    error instanceof TRPCClientError ? error.message : typeof error === 'string' ? error : '';
  if (msg.includes('SUPERSEDED_LABEL')) {
    return { isSuperseded: true, replacement: null };
  }
  return { isSuperseded: false, replacement: null };
}

export interface StocktakeDriftInfo {
  isDrift: boolean;
  /** Lines whose on-hand stock moved between submit and approve. */
  driftedLines: StocktakeDriftedLine[];
}

/**
 * BLU-711 A2: the client approved a count whose expected quantities no longer
 * match the shelf. Backend throws CONFLICT with cause
 * `{ code: 'STOCKTAKE_DRIFT', driftedLines }`, forwarded onto `data.cause` by
 * the errorFormatter's allowlist (see `parseSupersededError` for the same read).
 *
 * Deliberately NOT gated on `instanceof TRPCClientError`: this parser also runs
 * inside a react-query `onError`, where the argument is a `TRPCClientErrorLike`
 * shape rather than a guaranteed instance. The read itself is the same two-step
 * (`data.cause` first, `cause` second) `readForwardedCause` performs, and every
 * field is validated before it is trusted — there is no message-marker fallback
 * because the drifted lines only ever exist on the structured cause.
 */
export function parseStocktakeDrift(error: unknown): StocktakeDriftInfo {
  const cause = readCauseLike(error);
  if (!cause || cause.code !== 'STOCKTAKE_DRIFT' || !Array.isArray(cause.driftedLines)) {
    return { isDrift: false, driftedLines: [] };
  }
  const driftedLines = cause.driftedLines.filter(isDriftedLine);
  return { isDrift: true, driftedLines };
}

/**
 * The forwarded `cause.code` of a tRPC error, or undefined when there is none.
 *
 * Reads `data.cause` first and `cause` second, like every parser in this file,
 * and works on both a `TRPCClientError` instance and the `TRPCClientErrorLike`
 * shape react-query hands to `onError`. Only codes registered in the backend's
 * `FORWARDABLE_CAUSE_CODES` (apps/backend/src/trpc.ts) ever arrive: an
 * unregistered code is dropped there, so a new code is a two-file change.
 * Branch on the code, never on the English message.
 */
export function readCauseCode(error: unknown): string | undefined {
  const code = readCauseLike(error)?.code;
  return typeof code === 'string' ? code : undefined;
}

function readCauseLike(
  error: unknown,
): { code?: unknown; driftedLines?: unknown } | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const dataCause = (error as { data?: { cause?: unknown } }).data?.cause;
  if (dataCause && typeof dataCause === 'object') {
    return dataCause as { code?: unknown; driftedLines?: unknown };
  }
  const errCause = (error as { cause?: unknown }).cause;
  if (errCause && typeof errCause === 'object') {
    return errCause as { code?: unknown; driftedLines?: unknown };
  }
  return undefined;
}

/**
 * The message of a failed call: a bare string, an Error's message, the
 * `message` of a `TRPCClientErrorLike` shape, else ''.
 */
export function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return '';
}

/** A JSON object, or {} for anything else (bad JSON, an array, a scalar). */
function parseJsonObject(text: string): Record<string, unknown> {
  try {
    const value: unknown = JSON.parse(text);
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** `<PREFIX><KEY>:<json>` as the WHOLE message, one compiled pattern per prefix. */
const markerPatterns = new Map<string, RegExp>();
function markerPattern(prefix: string): RegExp {
  let pattern = markerPatterns.get(prefix);
  if (!pattern) {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    pattern = new RegExp(`^${escaped}([A-Z0-9_]+):(\\{[\\s\\S]*\\})$`);
    markerPatterns.set(prefix, pattern);
  }
  return pattern;
}

export interface MarkerErrorInfo<K extends string> {
  key: K;
  /** The refusal's detail: the marker's JSON, then the forwarded cause's fields over it; {} when unreadable. */
  detail: Record<string, unknown>;
}

/**
 * One parser for the `<PREFIX><KEY>:<json>` refusals (just-in-time POs, the
 * supplier catalog, Base garments). The server throws a TRPCError whose
 * MESSAGE is the marker and whose `cause` is `{ ...detail, code: <PREFIX><KEY> }`
 * (registered in FORWARDABLE_CAUSE_CODES).
 *
 * - The forwarded cause is read first and is the authority: one of ours gives
 *   the key, and a cause that is someone else's (another prefix, or a key not
 *   in `keys`) means the refusal is not ours, whatever the message says.
 * - With no cause (tRPC 11.7.1 drops wrapped causes on the wire today), the
 *   marker is the fallback. It must be the whole message, so an unrelated
 *   message that mentions a key never matches.
 * - The detail keeps both halves: the marker's JSON, with the cause's fields
 *   (minus `code`) over it, so a product `code` the cause cannot carry (its
 *   `code` is the cause code) still comes from the marker.
 *
 * Callers keep only their key list and their sentences.
 */
export function parseMarkerError<K extends string>(
  error: unknown,
  prefix: string,
  keys: readonly K[],
): MarkerErrorInfo<K> | null {
  const isKey = (value: string): value is K => (keys as readonly string[]).includes(value);
  const marker = markerPattern(prefix).exec(messageOf(error).trim());
  const markerDetail = marker?.[2] ? parseJsonObject(marker[2]) : {};

  const cause = readCauseLike(error) as Record<string, unknown> | undefined;
  const causeCode = typeof cause?.code === 'string' ? cause.code : undefined;
  if (causeCode !== undefined) {
    const key = causeCode.startsWith(prefix) ? causeCode.slice(prefix.length) : '';
    if (!isKey(key)) return null;
    const { code: _causeCode, ...rest } = cause ?? {};
    return { key, detail: { ...markerDetail, ...rest } };
  }
  const markerKey = marker?.[1];
  return markerKey && isKey(markerKey) ? { key: markerKey, detail: markerDetail } : null;
}

function isDriftedLine(value: unknown): value is StocktakeDriftedLine {
  if (!value || typeof value !== 'object') return false;
  const line = value as Record<string, unknown>;
  return (
    typeof line.skuId === 'string' &&
    typeof line.sku === 'string' &&
    typeof line.expectedQty === 'number' &&
    typeof line.currentStock === 'number' &&
    typeof line.countedQty === 'number'
  );
}

export interface DelegatedLocationBlockedInfo {
  /** True when the backend refused this write because the location is delegated. */
  isDelegatedLocationBlocked: boolean;
  /**
   * The operator sentence to show. Comes from the backend so the wording lives
   * in one place; falls back to the same copy if the message is ever empty.
   */
  message: string;
}

/**
 * BLU-1180: the database refused a stock or commitment INCREASE because the
 * chosen location has delegated its fulfilment to another warehouse (SQLSTATE
 * P1180). Backend throws BAD_REQUEST with cause
 * `{ code: 'DELEGATED_LOCATION_WRITE_BLOCKED' }`, forwarded onto `data.cause`
 * by the errorFormatter allowlist in `apps/backend/src/trpc.ts` — an
 * unregistered code is dropped there, so the two files move together.
 *
 * Deliberately NOT gated on `instanceof TRPCClientError` (same reasoning as
 * `parseStocktakeDrift`): this also runs inside react-query `onError`, where
 * the argument is a `TRPCClientErrorLike` shape. There is no message-marker
 * fallback on purpose — the refusal is identified by the structured code only,
 * so re-wording the sentence can never break detection.
 */
export function parseDelegatedLocationError(error: unknown): DelegatedLocationBlockedInfo {
  const cause = readCauseLike(error);
  if (!cause || cause.code !== 'DELEGATED_LOCATION_WRITE_BLOCKED') {
    return { isDelegatedLocationBlocked: false, message: '' };
  }
  const message =
    error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
      ? ((error as { message: string }).message)
      : '';
  return {
    isDelegatedLocationBlocked: true,
    message:
      message.length > 0
        ? message
        : 'Cannot increase stock or commitments at this location because fulfilment is delegated. Use the delegated warehouse instead.',
  };
}

/**
 * The text the batch-create marker parsers below read: a tRPC client error's
 * message, a bare string, any Error's message, else ''.
 */
function errorText(error: unknown): string {
  return typeof error === 'string' ? error : error instanceof Error ? error.message : '';
}

export interface SupersedeConfirmInfo {
  needsConfirm: boolean;
  /** Number of already-printed stickers a re-batch would invalidate. */
  count: number;
}

/**
 * Coordinator batch-create path: the backend throws CONFLICT
 * `SUPERSEDE_CONFIRM_REQUIRED:<count>` when a re-batch would invalidate <count>
 * already-printed-and-stuck stickers and `confirmSupersede` wasn't set. Parse the
 * count so the UI can show the "N shirts already have stickers" confirm dialog and
 * retry the same input with confirmSupersede:true.
 */
export function parseSupersedeConfirmError(error: unknown): SupersedeConfirmInfo {
  const msg = errorText(error);
  const match = msg.match(/SUPERSEDE_CONFIRM_REQUIRED:(\d+)/);
  if (match) {
    const n = Number(match[1]);
    return { needsConfirm: true, count: Number.isFinite(n) ? n : 0 };
  }
  return { needsConfirm: false, count: 0 };
}

export interface BatchTooLargeInfo {
  /** Garments the refused batch would have held. */
  count: number;
  /** The most garments one batch may hold. */
  max: number;
}

/**
 * Coordinator batch-create path: the backend refuses a batch over the garment
 * ceiling with a ValidationError whose message is
 * `BATCH_TOO_LARGE:<count>:<max>`. Same message-prefix pattern as
 * `parseSupersedeConfirmError`, because tRPC drops cause codes on the way to
 * the client until BLU-1277.
 *
 * Returns null for anything else, including a marker whose numbers are
 * missing, non-numeric or zero, so a garbled message falls through to the
 * caller's generic toast rather than rendering "0 garments".
 */
export function parseBatchTooLargeError(error: unknown): BatchTooLargeInfo | null {
  const msg = errorText(error);
  const match = msg.match(/BATCH_TOO_LARGE:(\d+):(\d+)/);
  if (!match) return null;
  const count = Number(match[1]);
  const max = Number(match[2]);
  if (!Number.isSafeInteger(count) || !Number.isSafeInteger(max) || count <= 0 || max <= 0) {
    return null;
  }
  return { count, max };
}

export type SupplyRefusalKind = 'alias_stock_write' | 'preprint_refused' | 'alias_read_only';

export interface SupplyRefusalInfo {
  kind: SupplyRefusalKind;
  /** The refused SKU code, or null when the server sent none (receipt reversal). */
  sku: string | null;
  /**
   * The 3PL's name, carried on the `SUPPLY_PREPRINT_REFUSED` cause. Always null
   * while causes are dropped on the wire, so callers resolve it themselves.
   */
  ownerName: string | null;
}

const SUPPLY_REFUSAL_KIND_BY_CODE: Readonly<Record<string, SupplyRefusalKind>> = {
  SUPPLY_ALIAS_STOCK_WRITE: 'alias_stock_write',
  SUPPLY_PREPRINT_REFUSED: 'preprint_refused',
  SUPPLY_ALIAS_READ_ONLY: 'alias_read_only',
};

// Built from the codes above, so a new refusal code is a one-place edit (the
// codes are plain capitals and underscores: nothing to escape).
const SUPPLY_REFUSAL_MARKER = new RegExp(`^(${Object.keys(SUPPLY_REFUSAL_KIND_BY_CODE).join('|')}):(.*)$`);

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Phase 92 (D-13): the refusals the server throws for a client product
 * built on the 3PL's blank (a supply alias):
 *
 * - `SUPPLY_ALIAS_STOCK_WRITE`: a single stock write (add, set, deduct, scrap,
 *   update, move to or from unavailable, receipt reversal, create SKU, and
 *   since BLU-1400 adding or removing its location row) on an alias. An alias
 *   holds no stock of its own.
 * - `SUPPLY_PREPRINT_REFUSED`: a pre-print of an alias. It prints to order.
 * - `SUPPLY_ALIAS_READ_ONLY` (BLU-1400): a catalog change to an alias (edit
 *   SKU, Batch edit, the Inventory edit). The alias is the 3PL's garment.
 *
 * All are thrown as BAD_REQUEST with `cause.code` set AND the message marker
 * `CODE:<sku>`. The cause code is read first and always wins when present.
 * The marker exists because tRPC drops every cause on the way to the client
 * until BLU-1277, so today detection runs on the marker alone. The marker is
 * anchored to the whole message: an unrelated message that merely mentions
 * the word "supply" never matches.
 *
 * Returns null for anything else. Callers show fixed copy for a refusal and
 * never print `error.message` for one.
 */
export function parseSupplyRefusal(error: unknown): SupplyRefusalInfo | null {
  const cause = readCauseLike(error) as
    | { code?: unknown; sku?: unknown; ownerName?: unknown }
    | undefined;
  const markerMatch = messageOf(error).trim().match(SUPPLY_REFUSAL_MARKER);
  const markerKind = markerMatch?.[1] ? SUPPLY_REFUSAL_KIND_BY_CODE[markerMatch[1]] : undefined;
  const markerSku = markerMatch ? nonEmptyString(markerMatch[2]) : null;

  if (cause && typeof cause.code === 'string') {
    const causeKind = SUPPLY_REFUSAL_KIND_BY_CODE[cause.code];
    // A forwarded cause code that is not a supply refusal wins over a marker.
    if (!causeKind) return null;
    return {
      kind: causeKind,
      sku: nonEmptyString(cause.sku) ?? (markerKind === causeKind ? markerSku : null),
      ownerName: nonEmptyString(cause.ownerName),
    };
  }

  if (!markerKind) return null;
  return { kind: markerKind, sku: markerSku, ownerName: null };
}

/** What the garment pre-print DTF refusal names: the first DTF SKU and the DTF item count. */
export interface PrePrintDtfRefusalInfo {
  sku: string | null;
  count: number;
}

const PREPRINT_DTF_REFUSAL_MARKER = /^PREPRINT_DTF_REFUSED:(\d+):(.+)$/;

/**
 * BLU-1420: garment pre-print is DTG only. `createPrePrintBatch` refuses a
 * batch holding any item that prints DTF with BAD_REQUEST, `cause.code`
 * `PREPRINT_DTF_REFUSED` ({ sku, count }) AND the message marker
 * `PREPRINT_DTF_REFUSED:<count>:<sku>` (backend twin:
 * `lib/preprint-dtf-errors.ts`). The forwarded cause is read first and wins;
 * any other forwarded code returns null. tRPC drops causes on the way to the
 * client until BLU-1277, so today detection runs on the marker, anchored to
 * the whole message (a message that merely mentions DTF never matches).
 * `count` defaults to 1 when unreadable. Callers show fixed copy, never
 * `error.message`.
 */
export function parsePrePrintDtfRefusal(error: unknown): PrePrintDtfRefusalInfo | null {
  const toCount = (value: unknown): number => {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    return Number.isSafeInteger(n) && n > 0 ? n : 1;
  };
  const cause = readCauseLike(error) as { code?: unknown; sku?: unknown; count?: unknown } | undefined;
  const marker = messageOf(error).trim().match(PREPRINT_DTF_REFUSAL_MARKER);

  if (cause && typeof cause.code === 'string') {
    if (cause.code !== 'PREPRINT_DTF_REFUSED') return null;
    return {
      sku: nonEmptyString(cause.sku) ?? (marker ? nonEmptyString(marker[2]) : null),
      count: toCount(cause.count ?? marker?.[1]),
    };
  }

  if (!marker) return null;
  return { sku: nonEmptyString(marker[2]), count: toCount(marker[1]) };
}

/**
 * Phase 92 (92-15): the print station's "Missing Artwork" report on a supply
 * garment whose stamped blank could not be read. The server answers
 * INTERNAL_SERVER_ERROR with the `SUPPLY_SOURCE_UNREADABLE:` message marker
 * (tRPC drops the cause). The station must show a retry message and must NOT
 * fall back to the generic `issues.create`, which files an unstamped issue
 * that would read as the client's own rules. Backend twin:
 * `SUPPLY_SOURCE_UNREADABLE_MARKER` in print-station.dao.ts.
 */
export function isSupplySourceUnreadable(error: unknown): boolean {
  return messageOf(error).includes('SUPPLY_SOURCE_UNREADABLE');
}

/**
 * Coordinator batch-create path: the backend allows one Create Batch per org
 * per location at a time and refuses another with CONFLICT
 * `BATCH_CREATION_IN_PROGRESS: ...`. Nothing was started, so the caller says
 * so and waits for the location to free. Same message-prefix pattern as
 * `parseSupersedeConfirmError` (cause codes are dropped until BLU-1277).
 */
export function isBatchCreationInProgressError(error: unknown): boolean {
  const msg = errorText(error);
  return /\bBATCH_CREATION_IN_PROGRESS\b/.test(msg);
}

/**
 * Coordinator batch-create path: createBatch refuses to mint a batch holding
 * a DTF-tagged order when it cannot read the org's DTF order tag setting
 * (backend `dtf-order-tags.ts`, `DTF_TAGS_UNREADABLE_BATCH_MESSAGE`): a unit's
 * print technology is stamped once, so the batch is rolled back instead. The
 * message starts with `DTF_ORDER_TAGS_UNREADABLE`; the marker rides on the
 * message because cause codes are dropped on the way to the client until
 * BLU-1277.
 */
export function isDtfOrderTagsUnreadableError(error: unknown): boolean {
  const msg = errorText(error);
  return /\bDTF_ORDER_TAGS_UNREADABLE\b/.test(msg);
}

const JSON_STRING = String.raw`"(?:[^"\\]|\\.)*"`;
const JSON_STRING_ARRAY = String.raw`\[(?:${JSON_STRING}(?:,${JSON_STRING})*)?\]`;

/** Right after the marker: the current `{"names":[...],"total":N}`, or the older bare JSON array of names. */
const ORDER_TAGS_CONFLICT_RE = new RegExp(
  String.raw`\bORDER_TAGS_CONFLICT(${JSON_STRING_ARRAY}|\{"names":${JSON_STRING_ARRAY},"total":\d+\})`
);

export interface OrderTagsConflict {
  /** Up to five of the conflicting orders' names. */
  names: string[];
  /** How many orders conflict in all; at least `names.length`. */
  total: number;
}

/**
 * Coordinator batch-create path: createBatch refuses a batch holding an order
 * that carries both a DTF tag and a DTG tag (backend `dtf-order-tags.ts`,
 * `orderTagsConflictBatchMessage`): its print technology would be a guess.
 * The message starts `ORDER_TAGS_CONFLICT{"names":["#1001","#1002"],"total":8}:`,
 * up to five order names and the total. An older server sends only the bare
 * array of names, whose length is then the total. Returns null for any other
 * error, including a marker whose list is missing, malformed or empty (the
 * caller's generic toast then shows the server text).
 */
export function parseOrderTagsConflictError(error: unknown): OrderTagsConflict | null {
  const match = errorText(error).match(ORDER_TAGS_CONFLICT_RE);
  if (!match) return null;
  try {
    const parsed: unknown = JSON.parse(match[1]!);
    const names: unknown = Array.isArray(parsed) ? parsed : (parsed as { names: unknown }).names;
    if (!Array.isArray(names) || names.length === 0 || !names.every((name) => typeof name === 'string')) return null;
    const total: unknown = Array.isArray(parsed) ? names.length : (parsed as { total: unknown }).total;
    if (typeof total !== 'number' || !Number.isSafeInteger(total)) return null;
    return { names, total: Math.max(total, names.length) };
  } catch {
    return null;
  }
}

/**
 * tRPC codes that say the server did not answer for the procedure itself: a
 * proxy or socket gave up first, so the work may still be running.
 */
const UNCERTAIN_OUTCOME_CODES = new Set([
  'TIMEOUT',
  'CLIENT_CLOSED_REQUEST',
  'BAD_GATEWAY',
  'SERVICE_UNAVAILABLE',
  'GATEWAY_TIMEOUT',
]);

/**
 * True when a mutation failed WITHOUT a verdict from its handler: the fetch
 * failed, the socket closed (createBatch's 120 s ceiling), or a proxy returned
 * a non-tRPC page (the HK proxy's 502). The handler may have finished, so the
 * caller must check rather than report "failed".
 *
 * Any tRPC-shaped error with another code IS the handler's verdict.
 */
export function isUncertainOutcomeError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const data = error.data as { code?: unknown } | undefined | null;
  if (!data || typeof data.code !== 'string' || data.code.length === 0) return true;
  return UNCERTAIN_OUTCOME_CODES.has(data.code);
}

/**
 * tRPC codes that map to httpStatus >= 500 (tRPC v11 status map). These are
 * server faults, never by-design guards — the fallback when httpStatus is
 * missing. SERVICE_UNAVAILABLE is live in prod (print-agent-admin throws it
 * when its storage/DB client fails to init).
 */
const TRPC_SERVER_ERROR_CODES = new Set([
  'INTERNAL_SERVER_ERROR',
  'NOT_IMPLEMENTED',
  'BAD_GATEWAY',
  'SERVICE_UNAVAILABLE',
  'GATEWAY_TIMEOUT',
]);

/**
 * Sentry hygiene: true when an error is a by-design tRPC guard (validation,
 * auth expiry, precondition, conflict, …) that should NOT be captured as a
 * Sentry error — i.e. a TRPCClientError carrying a structured `data.code`
 * with httpStatus < 500.
 *
 * Only the presence of `data.code` is trusted — never message strings. An
 * error WITHOUT a tRPC code (raw network failure, non-tRPC runtime error) or
 * with any 5xx code/status (e.g. a backend read failing on a Supabase 522,
 * or SERVICE_UNAVAILABLE from print-agent-admin) returns false so it keeps
 * being reported.
 */
export function isExpectedTrpcError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const data = error.data as { code?: unknown; httpStatus?: unknown } | undefined | null;
  if (!data || typeof data.code !== 'string' || data.code.length === 0) return false;
  // httpStatus is authoritative when present (backend errorFormatter always
  // populates it); the code set is the fallback for defensive parsing.
  if (typeof data.httpStatus === 'number') return data.httpStatus < 500;
  return !TRPC_SERVER_ERROR_CODES.has(data.code);
}

/**
 * The tRPC error code on a failed call (`CONFLICT`, `FORBIDDEN`,
 * `TOO_MANY_REQUESTS`, ...), or null when there is none (a network failure, a
 * non-tRPC error). Duck-typed on `data.code`, so a test double needs no
 * TRPCClientError. Branch on this, never on the message text.
 */
export function trpcErrorCode(error: unknown): string | null {
  const code = (error as { data?: { code?: unknown } } | null | undefined)?.data?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * Get user-friendly error message.
 */
export function getUserMessage(error: unknown): string {
  if (error instanceof TRPCClientError) {
    // Don't show raw prefixes to users
    if (error.message.includes('REDO_GARMENT:')) {
      return error.message.replace('REDO_GARMENT: ', '');
    }
    if (error.message.includes('INVENTORY_ERROR:')) {
      return error.message.replace('INVENTORY_ERROR: ', '');
    }
    if (error.message.includes('ALREADY_SCANNED:')) {
      return error.message.replace('ALREADY_SCANNED: ', '');
    }
    if (error.message.includes('OLD_LABEL:')) {
      return error.message.replace('OLD_LABEL: ', '');
    }
    if (error.message.includes('ORDER_ITEM_REMOVED:')) {
      return error.message.replace('ORDER_ITEM_REMOVED: ', '');
    }
    if (error.message.includes('ORDER_CHANGED_REMOVED:')) {
      return error.message.replace('ORDER_CHANGED_REMOVED: ', '');
    }
    if (error.message.includes('ORDER_CANCELLED:')) {
      return error.message.replace('ORDER_CANCELLED: ', '');
    }
    if (error.message.includes('PAP_PRODUCT:')) {
      return error.message.replace('PAP_PRODUCT: ', '');
    }
    if (/PENDING_LABELS:\d*:/.test(error.message)) {
      return error.message.replace(/PENDING_LABELS:\d*:\s*/, '');
    }
    if (/ORDER_CHANGED:[a-z_]*:/.test(error.message)) {
      return error.message.replace(/ORDER_CHANGED:[a-z_]*:\s*/, '');
    }
    if (/ORDER_ON_HOLD:[a-z_]*:/.test(error.message)) {
      return error.message.replace(/ORDER_ON_HOLD:[a-z_]*:\s*/, '');
    }
    if (error.message.includes('SUPERSEDED_LABEL:')) {
      return error.message.replace('SUPERSEDED_LABEL: ', '');
    }
    // Phase 50.1 film markers — stripped so a fallback path (bulk summary line,
    // generic modal) never shows the operator a raw signal token.
    if (error.message.includes('FILM_ARTWORK_TRANSIENT:')) {
      return error.message.replace('FILM_ARTWORK_TRANSIENT: ', '');
    }
    if (error.message.includes('FILM_SUPPLY_BLOCKED:')) {
      return error.message.replace('FILM_SUPPLY_BLOCKED: ', '');
    }
    if (error.message.includes('SUPPLY_SOURCE_UNREADABLE:')) {
      return error.message.replace('SUPPLY_SOURCE_UNREADABLE: ', '');
    }
    if (/FILM_SIDE_MISMATCH:(front|back)?:/.test(error.message)) {
      return error.message.replace(/FILM_SIDE_MISMATCH:(front|back)?:\s*/, '');
    }
    return error.message;
  }
  // Handle string errors (e.g., from useGarmentLookup which stores error.message)
  if (typeof error === 'string') {
    if (error.includes('REDO_GARMENT:')) {
      return error.replace('REDO_GARMENT: ', '');
    }
    if (error.includes('INVENTORY_ERROR:')) {
      return error.replace('INVENTORY_ERROR: ', '');
    }
    if (error.includes('ALREADY_SCANNED:')) {
      return error.replace('ALREADY_SCANNED: ', '');
    }
    if (error.includes('OLD_LABEL:')) {
      return error.replace('OLD_LABEL: ', '');
    }
    if (error.includes('ORDER_ITEM_REMOVED:')) {
      return error.replace('ORDER_ITEM_REMOVED: ', '');
    }
    if (error.includes('ORDER_CHANGED_REMOVED:')) {
      return error.replace('ORDER_CHANGED_REMOVED: ', '');
    }
    if (error.includes('ORDER_CANCELLED:')) {
      return error.replace('ORDER_CANCELLED: ', '');
    }
    if (error.includes('PAP_PRODUCT:')) {
      return error.replace('PAP_PRODUCT: ', '');
    }
    if (/PENDING_LABELS:\d*:/.test(error)) {
      return error.replace(/PENDING_LABELS:\d*:\s*/, '');
    }
    if (/ORDER_CHANGED:[a-z_]*:/.test(error)) {
      return error.replace(/ORDER_CHANGED:[a-z_]*:\s*/, '');
    }
    if (/ORDER_ON_HOLD:[a-z_]*:/.test(error)) {
      return error.replace(/ORDER_ON_HOLD:[a-z_]*:\s*/, '');
    }
    if (error.includes('SUPERSEDED_LABEL:')) {
      return error.replace('SUPERSEDED_LABEL: ', '');
    }
    if (error.includes('FILM_ARTWORK_TRANSIENT:')) {
      return error.replace('FILM_ARTWORK_TRANSIENT: ', '');
    }
    if (error.includes('FILM_SUPPLY_BLOCKED:')) {
      return error.replace('FILM_SUPPLY_BLOCKED: ', '');
    }
    if (error.includes('SUPPLY_SOURCE_UNREADABLE:')) {
      return error.replace('SUPPLY_SOURCE_UNREADABLE: ', '');
    }
    if (/FILM_SIDE_MISMATCH:(front|back)?:/.test(error)) {
      return error.replace(/FILM_SIDE_MISMATCH:(front|back)?:\s*/, '');
    }
    return error;
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred';
}
